import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { api, type ConnectorDetail, type ConnectorFile } from "@/lib/api";
import { connectorKeys, formatBytes, secretLines as findSecretLines, shortCommit } from "@/lib/connectors";
import { highlightLines, langOf } from "@/lib/highlight";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { FileText, Folder } from "lucide-react";
import { useMemo, useState } from "react";
import { CodeTokens, SecretBadge, Segmented } from "./parts";

type Which = "running" | "pending";

// Read-only files of the running version, with a switch to the version
// waiting for review. The bytes are what the checks and the review saw.
export function CodeBrowser({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const running = connector.running?.commit ?? null;
  const pending = connector.pending?.commit ?? null;
  const [picked, setPicked] = useState<Which>(running ? "running" : "pending");
  const which: Which = picked === "pending" && pending ? "pending" : running ? "running" : "pending";
  const commit = which === "running" ? running : pending;
  const [path, setPath] = useState<string | null>(null);

  const query = useQuery({
    queryKey: connectorKeys.files(containerId, connector.slug, commit ?? ""),
    queryFn: () => api.connectors.files(containerId, connector.slug, commit!),
    enabled: !!commit,
    staleTime: Infinity,
  });
  const files = query.data?.files;
  const selected =
    files?.find((f) => f.path === path) ?? files?.find((f) => f.path === "connector.yaml") ?? files?.[0] ?? null;

  if (!commit) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Code</h2>
        {running && pending ? (
          <Segmented
            label="Version"
            value={which}
            onChange={setPicked}
            options={(["running", "pending"] as const).map((w) => ({
              value: w,
              label: (
                <>
                  {w === "running" ? "Running" : "Waiting for review"}
                  <span className="ml-1.5 font-mono text-muted-foreground">{shortCommit(w === "running" ? running : pending)}</span>
                </>
              ),
            }))}
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {which === "running" ? "Running version" : "Waiting for review"}{" "}
            <span className="font-mono">{shortCommit(commit)}</span>
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">Read-only</span>
      </div>

      {query.isLoading ? (
        <div className="flex h-[32rem] rounded-lg border">
          <LoadingIndicator />
        </div>
      ) : query.isError ? (
        <InlineError error={query.error} />
      ) : !files || files.length === 0 ? (
        <p className="rounded-lg border p-4 text-sm text-muted-foreground">This version has no files.</p>
      ) : (
        <div className="flex h-[32rem] overflow-hidden rounded-lg border">
          <FileTree files={files} selected={selected?.path ?? null} onSelect={setPath} />
          <div className="flex min-w-0 flex-1 flex-col">
            {selected && <FileView key={`${commit}:${selected.path}`} file={selected} connector={connector} />}
          </div>
        </div>
      )}
    </section>
  );
}

type TreeNode = { name: string; path: string; file?: ConnectorFile; children: TreeNode[] };

function buildTree(files: ConnectorFile[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", children: [] };
  for (const f of files) {
    const parts = f.path.split("/");
    let node = root;
    parts.forEach((part, i) => {
      const path = parts.slice(0, i + 1).join("/");
      let child = node.children.find((c) => c.name === part && !c.file === (i < parts.length - 1));
      if (!child) {
        child = { name: part, path, children: [], ...(i === parts.length - 1 ? { file: f } : {}) };
        node.children.push(child);
      }
      node = child;
    });
  }
  // Folders first, then files, each by name.
  const sort = (n: TreeNode) => {
    n.children.sort((a, b) => Number(!!a.file) - Number(!!b.file) || a.name.localeCompare(b.name));
    n.children.forEach(sort);
  };
  sort(root);
  return root.children;
}

function FileTree({
  files,
  selected,
  onSelect,
}: {
  files: ConnectorFile[];
  selected: string | null;
  onSelect: (path: string) => void;
}) {
  const tree = useMemo(() => buildTree(files), [files]);
  const render = (nodes: TreeNode[], depth: number): React.ReactNode =>
    nodes.map((n) =>
      n.file ? (
        <button
          key={n.path}
          type="button"
          onClick={() => onSelect(n.path)}
          aria-current={selected === n.path ? "true" : undefined}
          title={n.path}
          className={cn(
            "flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs transition-colors",
            selected === n.path ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
          style={{ paddingLeft: `${0.5 + depth * 0.75}rem` }}
        >
          <FileText className="size-3.5 shrink-0" />
          <span className="truncate">{n.name}</span>
        </button>
      ) : (
        <div key={n.path}>
          <div
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground"
            style={{ paddingLeft: `${0.5 + depth * 0.75}rem` }}
          >
            <Folder className="size-3.5 shrink-0" />
            <span className="truncate">{n.name}</span>
          </div>
          {render(n.children, depth + 1)}
        </div>
      ),
    );
  return (
    <nav aria-label="Files" className="w-56 shrink-0 overflow-y-auto border-r bg-muted/20 p-1.5">
      {render(tree, 0)}
    </nav>
  );
}

function FileView({ file, connector }: { file: ConnectorFile; connector: ConnectorDetail }) {
  const lines = useMemo(
    () => (file.kind === "text" && file.content !== null ? highlightLines(file.content, langOf(file.path)) : null),
    [file],
  );
  const secretLines = useMemo(
    () => (file.kind === "text" ? findSecretLines(file.content, connector.variables) : new Set<number>()),
    [file, connector.variables],
  );

  return (
    <>
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
        <code className="min-w-0 flex-1 truncate font-mono text-xs">{file.path}</code>
        {secretLines.size > 0 && <SecretBadge>Uses a secret</SecretBadge>}
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatBytes(file.size)}</span>
      </div>
      {lines ? (
        <div className="min-h-0 flex-1 overflow-auto font-mono text-xs leading-5">
          <table className="border-collapse">
            <tbody>
              {lines.map((tokens, i) => (
                <tr key={i} className={cn(secretLines.has(i) && "bg-amber-400/20 dark:bg-amber-400/15")}>
                  <td className="sticky left-0 w-12 min-w-12 select-none bg-background px-2 text-right align-top text-muted-foreground/70 tabular-nums">
                    {i + 1}
                  </td>
                  <td className="whitespace-pre pr-6 pl-2">
                    <CodeTokens tokens={tokens} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {file.kind === "binary" ? "This file isn’t text, so it isn’t shown here." : "This file is too large to show here."}
        </div>
      )}
    </>
  );
}
